import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { registerPush, unregisterPush, updatePushTime, isPushEnabled } from '../services/pushService';
import { PUSH } from '../constants/strings';
import PasswordField from '../components/PasswordField';
import { useAuth } from '../context/AuthContext';
import {
  IconUser, IconPhoto, IconLock, IconNavAdmin, IconRating,
  IconNavFridge, IconDelete, IconNavSettings, IconWarning, IconCheck,
  IconBack, IconForward, IconQrAction, IconEdit,
} from '../components/icons';

const REASON_TAG_LABELS = {
  too_complex:   'Zu aufwendig',
  ingredients:   'Mag ich diese Zutaten nicht',
  wrong_cuisine: 'Falsche Küche',
  too_simple:    'Zu simpel',
};

const NAV_ITEMS = [
  { key: 'profil',      label: 'Profildaten', sub: 'Kontakt- und Profilinformationen',             Icon: IconUser },
  { key: 'bild',        label: 'Profilbild',  sub: 'Dein Avatar',                                  Icon: IconPhoto },
  { key: 'passwort',    label: 'Passwort',    sub: 'Passwort ändern',                               Icon: IconLock },
  { key: '2fa',         label: '2FA',         sub: 'Zwei-Faktor-Authentifizierung',                 Icon: IconQrAction },
  { key: 'bewertungen', label: 'Bewertungen', sub: 'Meine Rezept-Bewertungen',                      Icon: IconRating },
  { key: 'kuehlschrank',label: 'Kühlschrank', sub: 'Standard-Einstellungen für den Kühlschrank-Scan', Icon: IconNavFridge },
  { key: 'datenschutz', label: 'Datenschutz', sub: 'Account & Datenlöschung',                       Icon: IconDelete },
];

function UserSettings() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('profil');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'success'|'error', text }
  const [userThemesEnabled, setUserThemesEnabled] = useState(false);

  // Profildaten
  const [email, setEmail]         = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [about, setAbout]         = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [gender, setGender]       = useState('');
  const [username, setUsername]   = useState('');
  const [createdAt, setCreatedAt] = useState(null);

  // Profilbild
  const [avatarPath, setAvatarPath]     = useState('');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile]     = useState(null);
  const fileRef = useRef(null);

  // Passwort
  const [oldPw, setOldPw]   = useState('');
  const [newPw, setNewPw]   = useState('');
  const [newPw2, setNewPw2] = useState('');

  // 2FA
  const [totpEnabled,   setTotpEnabled]   = useState(false);
  const [totpRequired,  setTotpRequired]  = useState(false);
  const [totpSetupData, setTotpSetupData] = useState(null); // { secret, qr_code }
  const [totpSetupCode, setTotpSetupCode] = useState('');
  const [totpDisablePw, setTotpDisablePw] = useState('');
  const [totpDisableCode, setTotpDisableCode] = useState('');
  const [totpShowDisable, setTotpShowDisable] = useState(false);

  // Account-Löschung
  const [deletePw, setDeletePw]             = useState('');
  const [deleteConfirmed, setDeleteConfirmed] = useState(false);
  const [deleteLoading, setDeleteLoading]   = useState(false);
  const [deleteError, setDeleteError]       = useState('');
  const [deleteStep, setDeleteStep]         = useState(false); // true = Bestätigungsschritt
  const [exportLoading, setExportLoading]   = useState(false);
  const [pushEnabled, setPushEnabled]       = useState(false);
  const [pushLoading, setPushLoading]       = useState(false);
  const [pushSupported, setPushSupported]   = useState(true);
  const [pushTime, setPushTime]             = useState('08:00');

  // Kühlschrank-Einstellungen
  const [fridgePref, setFridgePref]         = useState('alltag');
  const [fridgePrefText, setFridgePrefText] = useState('');

  // Meine Bewertungen
  const [ratings, setRatings]           = useState([]);
  const [ratingsTotal, setRatingsTotal] = useState(0);
  const [ratingsPage, setRatingsPage]   = useState(1);
  const [ratingsPages, setRatingsPages] = useState(1);
  const [ratingsLoading, setRatingsLoading] = useState(false);
  const [deletingId, setDeletingId]     = useState(null);
  const [editingId, setEditingId]       = useState(null);
  const [editForm, setEditForm]         = useState({ liked: true, reason_tag: '', note: '' });
  const [savingEditId, setSavingEditId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.getProfile(),
      api.getPublicModules().catch(() => ({ modules: [] })),
      api.totpStatus().catch(() => ({ totp_enabled: false, totp_required: false })),
    ]).then(([profileData, modulesData, totpData]) => {
      const u = profileData.user;
      setUsername(u.username);
      setEmail(u.email || '');
      setFirstName(u.first_name || '');
      setLastName(u.last_name || '');
      setAbout(u.about || '');
      setBirthDate(u.birth_date || '');
      setGender(u.gender || '');
      setAvatarPath(u.avatar_path || '');
      setFridgePref(u.fridge_preference || 'alltag');
      setFridgePrefText(u.fridge_preference_text || '');
      setCreatedAt(u.created_at || null);
      const mods = modulesData.modules || [];
      setUserThemesEnabled(mods.some(m => m.name === 'user_themes' && m.enabled));
      setTotpEnabled(!!totpData.totp_enabled);
      setTotpRequired(!!totpData.totp_required);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function showMsg(type, text) {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  }

  // ── Tab Kühlschrank: Einstellungen speichern ──
  async function handleSaveFridge(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateProfile({ fridge_preference: fridgePref, fridge_preference_text: fridgePrefText });
      if (pushSupported && pushEnabled) {
        await updatePushTime(pushTime);
      }
      showMsg('success', 'Kühlschrank-Einstellungen gespeichert!');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Tab 1: Profildaten speichern ──
  async function handleSaveProfil(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateProfile({ email, first_name: firstName, last_name: lastName, about, birth_date: birthDate, gender });
      showMsg('success', 'Profil gespeichert!');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Tab 2: Avatar ──
  function handleAvatarSelect(e) {
    const file = e.target.files[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  }

  async function handleSaveAvatar(e) {
    e.preventDefault();
    if (!avatarFile) return;
    setSaving(true);
    try {
      const res = await api.uploadAvatar(avatarFile);
      setAvatarPath(res.avatar_path);
      setAvatarPreview(null);
      setAvatarFile(null);
      showMsg('success', 'Profilbild gespeichert!');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Tab 4: Account löschen ──
  async function handleDeleteAccount(e) {
    e.preventDefault();
    if (!deleteConfirmed) {
      setDeleteError('Bitte bestätige, dass du die Löschung verstehst.');
      return;
    }
    if (!deletePw) {
      setDeleteError('Bitte gib dein Passwort ein.');
      return;
    }
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await api.deleteAccount(deletePw);
      // Cookies wurden serverseitig gelöscht — logout() würde CSRF-Fehler werfen
      try { await logout(); } catch (_) {}
      navigate('/');
    } catch (err) {
      setDeleteError(err.message || 'Fehler beim Löschen des Accounts.');
    } finally {
      setDeleteLoading(false);
    }
  }

  // ── 2FA: Setup starten ──
  async function handleTotpSetup() {
    setSaving(true);
    try {
      const res = await api.totpSetup();
      setTotpSetupData(res);
      setTotpSetupCode('');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── 2FA: Aktivieren ──
  async function handleTotpEnable(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.totpEnable(totpSetupCode);
      setTotpEnabled(true);
      setTotpSetupData(null);
      setTotpSetupCode('');
      showMsg('success', '2FA erfolgreich aktiviert!');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── 2FA: Deaktivieren ──
  async function handleTotpDisable(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.totpDisable(totpDisablePw, totpDisableCode);
      setTotpEnabled(false);
      setTotpDisablePw('');
      setTotpDisableCode('');
      setTotpShowDisable(false);
      showMsg('success', '2FA deaktiviert.');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Tab Bewertungen: Laden ──
  async function loadRatings(page = 1) {
    setRatingsLoading(true);
    try {
      const res = await api.getFridgeFeedback(page, 20);
      setRatings(res.items || []);
      setRatingsTotal(res.total || 0);
      setRatingsPage(res.page || 1);
      setRatingsPages(res.pages || 1);
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setRatingsLoading(false);
    }
  }

  useEffect(() => {
    if (tab === 'bewertungen') loadRatings(1);
    if (tab === 'datenschutz' || tab === 'kuehlschrank') {
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setPushSupported(supported);
      if (supported) isPushEnabled().then(setPushEnabled).catch(() => setPushEnabled(false));
    }
  }, [tab]); // eslint-disable-line

  async function handleDeleteRating(id) {
    setDeletingId(id);
    try {
      await api.deleteFridgeFeedback(id);
      setRatings(prev => prev.filter(r => r.id !== id));
      setRatingsTotal(prev => prev - 1);
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setDeletingId(null);
    }
  }

  function startEdit(r) {
    setEditingId(r.id);
    setEditForm({ liked: r.liked, reason_tag: r.reason_tag || '', note: r.note || '' });
  }

  async function handleSaveEdit(id) {
    setSavingEditId(id);
    try {
      const updated = await api.updateFridgeFeedback(id, {
        liked:      editForm.liked,
        reason_tag: editForm.liked ? null : (editForm.reason_tag || null),
        note:       editForm.liked ? null : (editForm.note || null),
      });
      setRatings(prev => prev.map(r => r.id === id ? updated : r));
      setEditingId(null);
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSavingEditId(null);
    }
  }

  // ── Tab 3: Passwort ──
  async function handleChangePassword(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.changePassword(oldPw, newPw, newPw2);
      setOldPw(''); setNewPw(''); setNewPw2('');
      showMsg('success', 'Passwort erfolgreich geändert!');
    } catch (err) {
      showMsg('error', err.message);
    } finally {
      setSaving(false);
    }
  }

  const initial = (username || '?')[0].toUpperCase();
  const displayAvatar = avatarPreview || avatarPath || null;

  const visibleNavItems = [
    ...NAV_ITEMS.slice(0, 4),
    ...(userThemesEnabled ? [{ key: 'design', label: 'Design', sub: 'Erscheinungsbild', icon: <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></> }] : []),
    ...NAV_ITEMS.slice(4),
  ];
  const currentTab = visibleNavItems.find(i => i.key === tab) || visibleNavItems[0];

  if (loading) return <div className="settings-loading"><div className="loader" /><p>Lade Profil…</p></div>;

  return (
    <div className="settings-page fade-in">
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Einstellungen</div>
        </div>
      </div>

      {msg && <div className={`settings-msg settings-msg-${msg.type}`}>{msg.text}</div>}

      <div className="settings-grid">

        {/* ── Linke Navigation ── */}
        <div className="settings-nav">
          {visibleNavItems.map(item => (
            <button
              key={item.key}
              className={`settings-nav-item${tab === item.key ? ' active' : ''}`}
              onClick={() => {
                setTab(item.key);
                setTimeout(() => {
                  document.querySelector('.settings-nav-item.active')
                    ?.scrollIntoView({ block: 'nearest', inline: 'center' });
                }, 50);
              }}
            >
              <item.Icon size={16} />
              {item.label}
              {item.key === '2fa' && totpRequired && !totpEnabled && (
                <span style={{ marginLeft: 'auto', fontSize: '0.65rem', color: 'var(--red)', fontWeight: 800 }}>!</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Rechter Inhalt ── */}
        <div className="settings-content">
          <div className="settings-card">
            <div className="settings-card-head">
              <div className="settings-card-title">{currentTab.label}</div>
              <div className="settings-card-sub">{currentTab.sub}</div>
            </div>
            <div className="settings-card-body">

              {/* Avatar-Zeile */}
              <div className="profile-avatar-section">
                <div className="profile-avatar-big">
                  {displayAvatar ? <img src={displayAvatar} alt="Avatar" /> : initial}
                </div>
                <div className="profile-avatar-info">
                  <div className="profile-avatar-name">
                    {username}
                    {createdAt && new Date(createdAt) < new Date('2026-07-01') && (
                      <span className="settings-beta-badge">🦉 Beta-Pionier</span>
                    )}
                  </div>
                  <div className="profile-avatar-handle">@{username}</div>
                </div>
              </div>

              {/* ── Profildaten ── */}
              {tab === 'profil' && (
                <form onSubmit={handleSaveProfil}>
                  <div className="sec-title" style={{ marginBottom: 12 }}>
                    Kontaktdaten <span className="sec-title-note">(nicht öffentlich)</span>
                  </div>
                  <div className="editor-field">
                    <label className="field-label">E-Mail</label>
                    <input className="field-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="deine@email.de" />
                  </div>
                  <div className="field-row">
                    <div className="editor-field">
                      <label className="field-label">Vorname</label>
                      <input className="field-input" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Vorname" />
                    </div>
                    <div className="editor-field">
                      <label className="field-label">Nachname</label>
                      <input className="field-input" type="text" value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Nachname" />
                    </div>
                  </div>

                  <div className="sec-title" style={{ marginBottom: 12, marginTop: 20 }}>
                    Profilangaben <span className="sec-title-note">(öffentlich sichtbar)</span>
                  </div>
                  <div className="field-row">
                    <div className="editor-field">
                      <label className="field-label">Geschlecht</label>
                      <select className="field-select" value={gender} onChange={e => setGender(e.target.value)}>
                        <option value="">– keine Angabe –</option>
                        <option value="weiblich">Weiblich</option>
                        <option value="männlich">Männlich</option>
                        <option value="divers">Divers</option>
                      </select>
                    </div>
                    <div className="editor-field">
                      <label className="field-label">Geburtstag</label>
                      <input className="field-input" type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} />
                    </div>
                  </div>
                  <div className="editor-field">
                    <label className="field-label">Über mich</label>
                    <textarea className="field-input field-textarea" value={about} onChange={e => setAbout(e.target.value)} placeholder="Ein paar Worte über dich und deine Leidenschaft fürs Kochen…" rows={4} />
                  </div>
                  <div className="field-actions">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Speichern…' : 'Speichern'}
                    </button>
                  </div>
                </form>
              )}

              {/* ── Profilbild ── */}
              {tab === 'bild' && (
                <form onSubmit={handleSaveAvatar}>
                  <div className="avatar-upload-area">
                    <div className="avatar-preview-wrap">
                      {displayAvatar
                        ? <img src={displayAvatar} alt="Vorschau" className="avatar-preview-img" />
                        : <div className="avatar-preview-placeholder">{initial}</div>
                      }
                    </div>
                    <div className="avatar-upload-info">
                      <p>Wähle ein Bild für dein Profil. Empfohlen: quadratisch, min. 200×200 Pixel.</p>
                      <p className="avatar-hint">Unterstützte Formate: JPG, PNG, WebP – max. 3 MB</p>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        ref={fileRef}
                        onChange={handleAvatarSelect}
                        style={{ display: 'none' }}
                      />
                      <button type="button" className="btn btn-ghost" onClick={() => fileRef.current.click()}>
                        Bild auswählen
                      </button>
                      {avatarFile && <span className="avatar-filename">{avatarFile.name}</span>}
                    </div>
                  </div>
                  <div className="field-actions">
                    <button type="submit" className="btn btn-primary" disabled={saving || !avatarFile}>
                      {saving ? 'Hochladen…' : 'Profilbild speichern'}
                    </button>
                  </div>
                </form>
              )}

              {/* ── Passwort ── */}
              {tab === 'passwort' && (
                <form onSubmit={handleChangePassword}>
                  <div className="editor-field">
                    <label className="field-label">Benutzername</label>
                    <input className="field-input" type="text" value={username} disabled />
                    <span className="field-hint">Der Benutzername kann nicht geändert werden.</span>
                  </div>
                  <div className="editor-field">
                    <label className="field-label">Altes Passwort</label>
                    <PasswordField value={oldPw} onChange={e => setOldPw(e.target.value)} placeholder="Aktuelles Passwort" autoComplete="current-password" />
                  </div>
                  <div className="editor-field">
                    <label className="field-label">Neues Passwort</label>
                    <PasswordField value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Neues Passwort (min. 8 Zeichen)" autoComplete="new-password" showStrength showGenerator onGenerate={pw => { setNewPw(pw); setNewPw2(pw); }} />
                  </div>
                  <div className="editor-field">
                    <label className="field-label">Neues Passwort wiederholen</label>
                    <PasswordField value={newPw2} onChange={e => setNewPw2(e.target.value)} placeholder="Neues Passwort bestätigen" autoComplete="new-password" />
                  </div>
                  <div className="field-actions">
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? 'Ändern…' : 'Passwort ändern'}
                    </button>
                  </div>
                </form>
              )}

              {/* ── 2FA ── */}
              {tab === '2fa' && (
                <div>
                  {totpEnabled ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '1rem', background: 'var(--green-pale)', borderRadius: 8, marginBottom: '1.5rem' }}>
                        <span style={{ fontSize: '1.5rem' }}>✅</span>
                        <div>
                          <div style={{ fontWeight: 700, color: 'var(--green-dark)' }}>2FA ist aktiv</div>
                          <div className="field-hint" style={{ marginTop: 2 }}>Beim nächsten Login wird dein Authenticator-Code abgefragt.</div>
                        </div>
                      </div>
                      {!totpShowDisable ? (
                        <button type="button" className="btn btn-danger btn-sm" onClick={() => setTotpShowDisable(true)}>
                          2FA deaktivieren
                        </button>
                      ) : (
                        <form onSubmit={handleTotpDisable}>
                          <div className="sec-title" style={{ marginBottom: 12, color: 'var(--red)' }}>2FA deaktivieren</div>
                          <p className="field-hint" style={{ marginBottom: '1rem' }}>Gib dein Passwort und einen gültigen Code ein um 2FA zu deaktivieren.</p>
                          <div className="editor-field">
                            <label className="field-label">Aktuelles Passwort</label>
                            <PasswordField value={totpDisablePw} onChange={e => setTotpDisablePw(e.target.value)} placeholder="Passwort zur Bestätigung" autoComplete="current-password" />
                          </div>
                          <div className="editor-field">
                            <label className="field-label">Authenticator-Code</label>
                            <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                              value={totpDisableCode} onChange={e => setTotpDisableCode(e.target.value)}
                              placeholder="000000" required
                              style={{ fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: '0.2em', maxWidth: 160, padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--r)', background: 'var(--bg)', color: 'var(--txt)', outline: 'none' }} />
                          </div>
                          <div className="field-actions">
                            <button type="submit" className="btn btn-danger" disabled={saving || !totpDisablePw || totpDisableCode.length !== 6}>
                              {saving ? 'Deaktiviere…' : '2FA deaktivieren'}
                            </button>
                            <button type="button" className="btn btn-ghost" onClick={() => { setTotpShowDisable(false); setTotpDisablePw(''); setTotpDisableCode(''); }}>
                              Abbrechen
                            </button>
                          </div>
                        </form>
                      )}
                    </>
                  ) : (
                    <>
                      {totpRequired && (
                        <div className="settings-msg settings-msg-error" style={{ marginBottom: '1.25rem' }}>
                          Der Administrator hat 2FA als Pflicht aktiviert. Bitte richte es jetzt ein.
                        </div>
                      )}
                      <p className="field-hint" style={{ marginBottom: '1.25rem' }}>
                        Schütze dein Konto mit einer Authenticator-App (z.B. Google Authenticator, Aegis).
                      </p>
                      {!totpSetupData ? (
                        <div className="field-actions" style={{ justifyContent: 'flex-start' }}>
                          <button type="button" className="btn btn-primary" onClick={handleTotpSetup} disabled={saving}>
                            {saving ? 'Lade…' : '2FA einrichten'}
                          </button>
                        </div>
                      ) : (
                        <form onSubmit={handleTotpEnable}>
                          <div className="editor-field">
                            <label className="field-label">Schritt 1 — QR-Code scannen</label>
                            <img src={totpSetupData.qr_code} alt="QR Code" style={{ width: 180, height: 180, display: 'block', margin: '0.5rem 0' }} />
                            <span className="field-hint">Oder Secret manuell eingeben: <strong style={{ fontFamily: 'monospace' }}>{totpSetupData.secret}</strong></span>
                          </div>
                          <div className="editor-field">
                            <label className="field-label">Schritt 2 — Code aus der App eingeben</label>
                            <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                              value={totpSetupCode} onChange={e => setTotpSetupCode(e.target.value)}
                              placeholder="000000" autoFocus required
                              style={{ fontFamily: 'monospace', fontSize: '1.2rem', letterSpacing: '0.2em', maxWidth: 160, padding: '8px 12px', border: '1.5px solid var(--border)', borderRadius: 'var(--r)', background: 'var(--bg)', color: 'var(--txt)', outline: 'none' }} />
                            <span className="field-hint">Der Code bestätigt dass die App korrekt eingerichtet ist.</span>
                          </div>
                          <div className="field-actions">
                            <button type="submit" className="btn btn-primary" disabled={saving || totpSetupCode.length !== 6}>
                              {saving ? 'Aktiviere…' : <><IconCheck size={14} /> 2FA aktivieren</>}
                            </button>
                            <button type="button" className="btn btn-ghost" onClick={() => setTotpSetupData(null)}>
                              Abbrechen
                            </button>
                          </div>
                        </form>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Bewertungen ── */}
              {tab === 'bewertungen' && (
                <div>
                  <p className="field-hint" style={{ marginBottom: '1rem' }}>
                    Diese Bewertungen nutzt die KI um dir beim nächsten Kühlschrank-Scan passendere Rezepte vorzuschlagen.
                    {ratingsTotal > 0 && <span className="ratings-count">{ratingsTotal}</span>}
                  </p>
                  {ratingsLoading ? (
                    <div className="ratings-loading"><div className="loader" /></div>
                  ) : ratings.length === 0 ? (
                    <div className="ratings-empty">
                      <p>Noch keine Bewertungen vorhanden.</p>
                      <p className="field-hint">Bewerte Rezeptvorschläge im Kühlschrank-Scan mit 👍 oder 👎.</p>
                    </div>
                  ) : (
                    <>
                      <div className="ratings-list">
                        {ratings.map(r => (
                          <div key={r.id} className={`ratings-item ${(editingId === r.id ? editForm.liked : r.liked) ? 'ratings-item--like' : 'ratings-item--dislike'}`}>
                            {editingId === r.id ? (
                              <div className="ratings-edit-form">
                                <div className="ratings-edit-title">{r.recipe_title}</div>
                                <div className="ratings-edit-toggle">
                                  <button type="button" className={`ratings-toggle-btn ${editForm.liked ? 'active-like' : ''}`} onClick={() => setEditForm(f => ({ ...f, liked: true, reason_tag: '', note: '' }))}>👍 Hat mir gefallen</button>
                                  <button type="button" className={`ratings-toggle-btn ${!editForm.liked ? 'active-dislike' : ''}`} onClick={() => setEditForm(f => ({ ...f, liked: false }))}>👎 Hat mir nicht gefallen</button>
                                </div>
                                {!editForm.liked && (
                                  <>
                                    <select className="ratings-edit-select" value={editForm.reason_tag} onChange={e => setEditForm(f => ({ ...f, reason_tag: e.target.value }))}>
                                      <option value="">— Grund wählen (optional) —</option>
                                      {Object.entries(REASON_TAG_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                    </select>
                                    <textarea className="ratings-edit-note" placeholder="Anmerkung (optional)" value={editForm.note} onChange={e => setEditForm(f => ({ ...f, note: e.target.value }))} rows={2} maxLength={500} />
                                  </>
                                )}
                                <div className="ratings-edit-actions">
                                  <button className="btn btn-primary btn-sm" onClick={() => handleSaveEdit(r.id)} disabled={savingEditId === r.id}>{savingEditId === r.id ? 'Speichern…' : 'Speichern'}</button>
                                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingId(null)} disabled={savingEditId === r.id}>Abbrechen</button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="ratings-item-icon">{r.liked ? '👍' : '👎'}</div>
                                <div className="ratings-item-body">
                                  <div className="ratings-item-title">{r.recipe_title}</div>
                                  {r.recipe_category && <div className="ratings-item-category">{r.recipe_category}</div>}
                                  {!r.liked && (r.reason_tag || r.note) && (
                                    <div className="ratings-item-reason">
                                      {r.reason_tag && <span className="ratings-reason-tag">{REASON_TAG_LABELS[r.reason_tag] || r.reason_tag}</span>}
                                      {r.note && <span className="ratings-item-note">„{r.note}"</span>}
                                    </div>
                                  )}
                                  <div className="ratings-item-date">{new Date(r.created_at).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' })}</div>
                                </div>
                                <div className="ratings-item-actions">
                                  <button className="ratings-action-btn" onClick={() => startEdit(r)} title="Bearbeiten">
                                    <IconEdit size={15} />
                                  </button>
                                  <button className="ratings-action-btn ratings-action-btn--danger" onClick={() => handleDeleteRating(r.id)} disabled={deletingId === r.id} title="Löschen">
                                    {deletingId === r.id ? <span className="ratings-delete-spinner" /> : <IconDelete size={15} />}
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                      {ratingsPages > 1 && (
                        <div className="ratings-pagination">
                          <button className="btn btn-secondary btn-sm" disabled={ratingsPage <= 1} onClick={() => { const p = ratingsPage - 1; setRatingsPage(p); loadRatings(p); }}><IconBack size={14} /> Zurück</button>
                          <span className="ratings-page-info">Seite {ratingsPage} / {ratingsPages}</span>
                          <button className="btn btn-secondary btn-sm" disabled={ratingsPage >= ratingsPages} onClick={() => { const p = ratingsPage + 1; setRatingsPage(p); loadRatings(p); }}>Weiter <IconForward size={14} /></button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Kühlschrank ── */}
              {tab === 'kuehlschrank' && (
                <form onSubmit={handleSaveFridge}>
                  <p className="field-hint" style={{ marginBottom: '1.25rem' }}>
                    Diese Einstellung wird beim Kühlschrank-Scan als Standard vorausgewählt. Du kannst sie vor jedem Scan noch ändern.
                  </p>
                  <div className="editor-field">
                    <label className="editor-label">Standard-Kochaufwand</label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                      {[
                        { key: 'schnell',   label: 'Schnell',   hint: '≤ 30 Min.' },
                        { key: 'alltag',    label: 'Alltag',    hint: 'Hausmannskost' },
                        { key: 'aufwendig', label: 'Aufwendig', hint: 'Mehr Zeit' },
                      ].map(opt => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setFridgePref(opt.key)}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                            padding: '8px 18px', borderRadius: 'var(--r2)', cursor: 'pointer',
                            border: `1.5px solid ${fridgePref === opt.key ? 'var(--green)' : 'var(--border)'}`,
                            background: fridgePref === opt.key ? 'var(--green-pale)' : 'var(--surface)',
                            color: fridgePref === opt.key ? 'var(--green-dark)' : 'var(--txt)',
                            fontWeight: 600, fontSize: '0.88rem',
                          }}
                        >
                          {opt.label}
                          <span style={{ fontSize: '0.72rem', fontWeight: 400, color: fridgePref === opt.key ? 'var(--green-dark)' : 'var(--txt3)' }}>{opt.hint}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="editor-field" style={{ marginTop: 16 }}>
                    <label className="editor-label">Eigene Wünsche (Standard)</label>
                    <textarea
                      className="editor-input"
                      placeholder="z.B. vegetarisch, für Kinder, wenig Kohlenhydrate, mediterran, ohne Zwiebeln"
                      value={fridgePrefText}
                      onChange={e => setFridgePrefText(e.target.value)}
                      rows={3}
                      style={{ resize: 'vertical' }}
                    />
                  </div>
                  {/* ── MHD-Erinnerung Uhrzeit ── */}
                  {pushSupported && pushEnabled && (
                    <div className="editor-field" style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
                      <label className="editor-label">{PUSH.timeLabel}</label>
                      <p className="field-hint" style={{ marginBottom: '0.75rem' }}>{PUSH.timeHint}</p>
                      <input
                        type="time"
                        value={pushTime}
                        onChange={e => setPushTime(e.target.value)}
                        style={{ padding: '0.3rem 0.5rem', borderRadius: 6, border: '1px solid var(--border)', fontSize: '0.95rem' }}
                      />
                    </div>
                  )}

                  <button type="submit" className="btn btn-secondary" disabled={saving} style={{ marginTop: 20 }}>
                    {saving ? 'Speichern…' : 'Einstellungen speichern'}
                  </button>
                </form>
              )}

              {/* ── Datenschutz ── */}
              {tab === 'datenschutz' && (
                <div>

                  {/* ── Push-Benachrichtigungen ── */}
                  <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: 600, marginBottom: '0.4rem' }}>{PUSH.sectionTitle}</div>
                    <p className="field-hint" style={{ marginBottom: '1rem' }}>{PUSH.sectionDesc}</p>
                    {!pushSupported ? (
                      <p className="field-hint">{PUSH.errNotSupported}</p>
                    ) : (
                      <>
                        {pushEnabled && (
                          <p className="field-hint" style={{ marginBottom: '0.75rem', color: 'var(--green)' }}>
                            <IconCheck size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                            {PUSH.stateActive}
                          </p>
                        )}
                        <button
                          type="button"
                          className={pushEnabled ? 'btn btn-ghost' : 'btn btn-secondary'}
                          disabled={pushLoading}
                          onClick={async () => {
                            setPushLoading(true);
                            try {
                              if (pushEnabled) {
                                await unregisterPush();
                                setPushEnabled(false);
                                showMsg('success', PUSH.successDisabled);
                              } else {
                                await registerPush(pushTime);
                                setPushEnabled(true);
                                showMsg('success', PUSH.successEnabled);
                              }
                            } catch (err) {
                              const msg = err.message || '';
                              if (msg.includes('abgelehnt') || msg.includes('denied')) {
                                showMsg('error', PUSH.errDenied);
                              } else if (msg.includes('unterstützt')) {
                                showMsg('error', PUSH.errNotSupported);
                              } else {
                                showMsg('error', PUSH.errGeneric);
                              }
                            } finally {
                              setPushLoading(false);
                            }
                          }}
                        >
                          {pushLoading ? '…' : (pushEnabled ? PUSH.btnDisable : PUSH.btnEnable)}
                        </button>
                        <p className="field-hint" style={{ marginTop: '0.75rem', fontSize: '0.8rem' }}>{PUSH.iosHint}</p>
                      </>
                    )}
                  </div>

                  <p className="field-hint" style={{ marginBottom: '0.5rem' }}>
                    Gemäß DSGVO Art. 20 hast du das Recht auf Datenübertragbarkeit.
                  </p>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ marginBottom: '0.5rem' }}
                    disabled={exportLoading}
                    onClick={async () => {
                      setExportLoading(true);
                      try { await api.exportUserData(); }
                      catch (err) { showMsg('error', err.message || 'Export fehlgeschlagen'); }
                      finally { setExportLoading(false); }
                    }}
                  >
                    {exportLoading ? 'Wird vorbereitet…' : 'Meine Daten exportieren'}
                  </button>
                  <p className="field-hint" style={{ marginBottom: '2rem' }}>
                    Als JSON-Datei — kann mit jedem Texteditor geöffnet werden.
                  </p>

                  <p className="field-hint" style={{ marginBottom: '1.5rem' }}>
                    Gemäß DSGVO Art. 17 hast du das Recht auf Löschung deiner personenbezogenen Daten.
                    Alle deine Rezepte, Bilder und Profildaten werden unwiderruflich gelöscht.
                  </p>
                  {!deleteStep ? (
                    <button type="button" className="btn btn-danger" onClick={() => { setDeleteStep(true); setDeleteError(''); }}>
                      Account löschen
                    </button>
                  ) : (
                    <form onSubmit={handleDeleteAccount}>
                      <div style={{ padding: '1.25rem', background: 'var(--red-pale)', border: '1.5px solid var(--red)', borderRadius: 8, marginBottom: '1.5rem' }}>
                        <div style={{ fontWeight: 700, color: 'var(--red)', marginBottom: '0.5rem', fontSize: '1rem' }}>
                          <IconWarning size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                          Achtung – diese Aktion ist unwiderruflich
                        </div>
                        <p className="field-hint" style={{ marginBottom: 0 }}>
                          Alle deine Daten (Rezepte, Bilder, Profilinfos) werden dauerhaft und vollständig gelöscht.
                        </p>
                      </div>
                      <div className="editor-field">
                        <label className="field-label">Passwort zur Bestätigung</label>
                        <PasswordField value={deletePw} onChange={e => setDeletePw(e.target.value)} placeholder="Dein aktuelles Passwort" autoComplete="current-password" />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', marginBottom: 16 }} onClick={() => setDeleteConfirmed(v => !v)}>
                        <input type="checkbox" checked={deleteConfirmed} onChange={e => setDeleteConfirmed(e.target.checked)} style={{ marginTop: 3, flexShrink: 0, cursor: 'pointer' }} onClick={e => e.stopPropagation()} />
                        <span style={{ fontSize: '0.9rem', color: 'var(--txt)' }}>Ich verstehe, dass alle meine Daten unwiderruflich gelöscht werden.</span>
                      </div>
                      {deleteError && <div className="settings-msg settings-msg-error" style={{ marginBottom: '1rem' }}>{deleteError}</div>}
                      <div className="field-actions">
                        <button type="submit" className="btn btn-danger" disabled={deleteLoading || !deleteConfirmed || !deletePw}>
                          {deleteLoading ? 'Lösche…' : 'Account endgültig löschen'}
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => { setDeleteStep(false); setDeletePw(''); setDeleteConfirmed(false); setDeleteError(''); }}>
                          Abbrechen
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default UserSettings;
